/**
 * notifications.js
 */

import React from 'react';
import axios from 'axios';
import { Box, Button, Drawer, List, ListItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useSnackbar } from 'notistack';
import useAsyncInterval from 'utils/use-async-interval';
import { NOTIFICATION_ANIMATION_DURATION, Types, isBitfieldContainingBitmask } from "utils/constants";
import NotificationsContext from 'contexts/notifications/notifications.context';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import Spinner from 'components/spinner';
import NotificationItem from './notification-item';
import { PersistentToastNotification } from './index';


const useStyles = makeStyles(theme => ({
  drawer: {
    width: theme.size.notificationDrawer,
    maxWidth: '100%',
    overflow: 'hidden',
    '& .MuiButton-root': {
      textTransform: 'none',
      padding: `${theme.spacing(0.25)} ${theme.spacing(2)}`
    }
  },
  topSection: {
    boxShadow: theme.boxShadowMaker(2, theme.palette.grey.colors[6]),
    backgroundColor: theme.palette.sys.surfaceContainerHighest
  },
  closeIcon: {
    height: theme.spacing(7),
    width: theme.spacing(7),
    padding: 0,
  },
  list: {
    padding: 0,
    paddingBottom: 400,
    overflowY: 'auto'
  },
  snackbarRoot: {
    display: 'flex',
    justifyContent: 'end',
  },
}));


const animationStyles = makeStyles(theme => ({
  enter: {
    opacity: 0.01,
    maxHeight: 0,
  },
  enterActive: {
    opacity: 1,
    maxHeight: 250,
  },
  exit: {
    opacity: 1,
    maxHeight: 250,
  },
  exitActive: {
    opacity: 0.01,
    maxHeight: 0,
  },
}));


export default function Notifications(props) {
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const userId = user?.id;
  const {
    notifications,
    unreadCount,
    decrementUnreadCount,
    open,
    setOpen,
    setNotifications,
    setMultiple
  } = React.useContext(NotificationsContext);
  const animationClasses = animationStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [activeSnackbarIds, setActiveSnackbarIds] = React.useState([]);
  const numNotifsToDisplay = 1;


  // fetches and sorts notifications
  const getNotifications = React.useCallback(async () => {
    const sortByUnread = (a, b) => {
      return a.isRead === b.isRead
        ? 0
        : a.isRead && !b.isRead
          ? 1
          : -1;
    }

    if (userId) {   
      const response = await axios.get('/account/notifications');
      if (response.isSuccess) {
        setMultiple({
          ...response.data,
          notifications: response.data.notifications.sort(sortByUnread)
        });
      }
    }
  }, [userId, setMultiple]);


  // check for notifications every minute
  useAsyncInterval(getNotifications, 60000);


  // trigger refreshing
  React.useEffect(() => {
    if (open || userId) {
      getNotifications();
    }
  }, [open, userId, getNotifications]);


  // close drawer
  const closeDrawer = () => {
    setOpen(false);
  }


  // sends list of notification objects to server for making as "read"
  const postReadNotifications = React.useCallback((notifications)  => {
    // update server
    axios.post('/account/notifications', notifications);
  }, []);


  // dismiss toast notifications by given list of ids
  const closePersistentToastNotificationsIfOpen = React.useCallback((notificationIdsToClose) => {
    notificationIdsToClose.forEach(nId => closeSnackbar(nId));
    setActiveSnackbarIds(prevIds => prevIds.filter(id => !notificationIdsToClose.includes(id)));
  }, [closeSnackbar]);


  // mark a single notification as "read"
  const markRead = React.useCallback((notification) => {
    if (!notification.isRead) {
      notification.isRead = true;
      postReadNotifications([notification]);
      setNotifications(notifications.map(n => n.id === notification.id ? notification : n));
      decrementUnreadCount();
      closePersistentToastNotificationsIfOpen([notification.id]);
    }
  }, [
    notifications,
    setNotifications,
    decrementUnreadCount, 
    closePersistentToastNotificationsIfOpen, 
    postReadNotifications
  ]);


  // mark all notifications as "read"
  const markAllRead = React.useCallback(() => {
    const unread = notifications.filter(n => !n.isRead);
    

    postReadNotifications(unread);
    setMultiple({
      notifications: notifications.map(notification => ({
        ...notification,
        isRead: true
      })),
      unreadCount: 0
    });
    closePersistentToastNotificationsIfOpen(unread.map(n => n.id));    
  }, [
    notifications, 
    closePersistentToastNotificationsIfOpen, 
    postReadNotifications, 
    setMultiple
  ]);


  // determine what notifications to toast and show them.
  React.useEffect(() => {
    if (unreadCount > 0) {
      let persistentToastNotifications = notifications
        .filter(n => !n.isRead && isBitfieldContainingBitmask(n.destinations, Types.NotificationDestination.PERSISTENT_TOAST));

      const ptNotifsToShow = persistentToastNotifications.slice(0, numNotifsToDisplay);
      const ptNotifIdsToShow = ptNotifsToShow.map(n => n.id);

      // Close appropriate PersistentToastNotifications first
      const activeSnackbarIdsNotToBeShowing = activeSnackbarIds.filter(activeSnackbarId => !ptNotifIdsToShow.includes(activeSnackbarId));
      if (activeSnackbarIdsNotToBeShowing.length > 0) {
        closePersistentToastNotificationsIfOpen(activeSnackbarIdsNotToBeShowing);
        return;
      }

      for (const persistentToastNotification of ptNotifsToShow.reverse()) {
        if (activeSnackbarIds.length < numNotifsToDisplay && !activeSnackbarIds.includes(persistentToastNotification.id)) {
          enqueueSnackbar(
            '',
            {
              content: (key, message) => <PersistentToastNotification
                id={key}
                message={message}
                notification={persistentToastNotification}
                onDismiss={() => markRead(persistentToastNotification)}
                onDismissAll={markAllRead}
                unreadCount={unreadCount}
              />,
              variant: 'info',
              key: persistentToastNotification.id,
              anchorOrigin: {
                horizontal: 'right',
                vertical: 'bottom',
              },
              persist: true,
              preventDuplicate: true,
            }
          );
          setActiveSnackbarIds(prevIds => [...prevIds, persistentToastNotification.id])
        }
      }
    }
  }, [
    notifications, 
    unreadCount, 
    classes, 
    numNotifsToDisplay, 
    activeSnackbarIds, 
    closePersistentToastNotificationsIfOpen,
    enqueueSnackbar, 
    markRead,
    markAllRead
  ]);


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeDrawer}
      classes={{ paper: classes.drawer }}
    >
      {/* containing div is needed to show top buttons properly in Safari  */}
      <Box className={classes.topSection}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          {/* close drawer button */}
          <Button
            className={`${classes.topItem} ${classes.closeIcon}`}
            onClick={closeDrawer}
            aria-label='close notifications'
            children={<Icon type='close' fontSize='large' />}
          />
          {/* dismiss all box */}
          <Box display="flex" justifyContent="center">
            <Button
              color="primary"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              children='DISMISS ALL'
            />
          </Box>
        </Box>
      </Box>

      <List className={classes.list} data-testid='notification-list'>
        <TransitionGroup className="notification-list">

          {/* regular list */}
          {notifications && notifications.map(notification => {
            return (
              <CSSTransition
                key={notification.id}
                timeout={NOTIFICATION_ANIMATION_DURATION}
                classNames={{ ...animationClasses }}
              >
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  markRead={markRead}
                  closeDrawer={closeDrawer}
                  onLink={closeDrawer}
                />
              </CSSTransition>
            );
          })}

          {/* empty list */}
          {(!notifications || notifications.length === 0) && <CSSTransition
            key={0}
            timeout={NOTIFICATION_ANIMATION_DURATION}
            classNames={{ ...animationClasses }}
          >
            <>
              <ListItem className={classes.item} style={{ paddingTop: "100px", justifyContent: "center" }}>
                <Spinner />
              </ListItem>
              <ListItem className={classes.item} style={{ justifyContent: "center" }}>
                <Typography variant="h6" className={classes.title}>
                  Loading notifications...
                </Typography>
              </ListItem>
            </>
          </CSSTransition>}

        </TransitionGroup>
      </List>
    </Drawer>
  );
}